import type { NewsPublisherId } from '../constants/newsPublishers';
import { DEFAULT_RANKING_PROMPT } from './newsCursorRank';
import {
  buildDefaultNewsFeedPreferences,
  DEFAULT_NEWS_SUBREDDITS,
  type NewsFeedPreferences,
} from './newsFeedDefaults';

export type NewsFeedConfigFile = {
  version?: number;
  subreddits?: unknown;
  publishers?: Partial<Record<NewsPublisherId, boolean>>;
  maxPostsPerSub?: number;
  maxPerPublisher?: number;
  maxRankedStories?: number;
  rankingPrompt?: string;
  /** @deprecated ignored — use rankingPrompt */
  interest?: string;
  /** @deprecated ignored */
  sourceWeights?: string;
  blocklist?: string;
};

export function parseNewsFeedConfigJson(raw: string): NewsFeedPreferences {
  const defaults = buildDefaultNewsFeedPreferences();
  let parsed: NewsFeedConfigFile;
  try {
    parsed = JSON.parse(raw) as NewsFeedConfigFile;
  } catch {
    return defaults;
  }

  const rankingPrompt =
    typeof parsed.rankingPrompt === 'string' && parsed.rankingPrompt.trim()
      ? parsed.rankingPrompt
      : typeof parsed.interest === 'string' && parsed.interest.trim()
        ? parsed.interest
        : defaults.rankingPrompt;

  return {
    subreddits: normalizeSubreddits(parsed.subreddits ?? defaults.subreddits),
    blocklistText:
      typeof parsed.blocklist === 'string' ? parsed.blocklist : defaults.blocklistText,
    maxPostsPerSub: clamp(parsed.maxPostsPerSub, 3, 25, defaults.maxPostsPerSub),
    publishersEnabled: normalizePublishers(parsed.publishers, defaults.publishersEnabled),
    maxPerPublisher: clamp(parsed.maxPerPublisher, 3, 20, defaults.maxPerPublisher),
    rankingPrompt,
    maxRankedStories: clamp(parsed.maxRankedStories, 10, 60, defaults.maxRankedStories),
  };
}

function normalizeSubreddits(subs: unknown): string[] {
  if (!Array.isArray(subs)) return [...DEFAULT_NEWS_SUBREDDITS];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const s of subs) {
    if (typeof s !== 'string') continue;
    const name = s.trim().replace(/^\/?r\//i, '');
    if (!name || !/^[A-Za-z0-9_]+$/.test(name)) continue;
    const key = name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(name);
  }
  return out.length > 0 ? out : [...DEFAULT_NEWS_SUBREDDITS];
}

function normalizePublishers(
  input: Partial<Record<NewsPublisherId, boolean>> | undefined,
  defaults: Record<NewsPublisherId, boolean>
): Record<NewsPublisherId, boolean> {
  return {
    bbc: input?.bbc ?? defaults.bbc,
    bbcmundo: input?.bbcmundo ?? defaults.bbcmundo,
    guardian: input?.guardian ?? defaults.guardian,
    elpais: input?.elpais ?? defaults.elpais,
    hackernews: input?.hackernews ?? defaults.hackernews,
  };
}

function clamp(n: unknown, min: number, max: number, fallback: number): number {
  if (typeof n !== 'number' || !Number.isFinite(n)) return fallback;
  return Math.max(min, Math.min(max, Math.round(n)));
}
